import {Icon} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {anchorElementId, novaInstanceKey, NovaAgentPicker, type NovaAgentPickerAlign} from './NovaAgentPicker';
import {showChangeAgentConfirmationModal} from './modals/ChangeAgentConfirmationModal';
import {NovaCompactButton} from './NovaCompactButton';

// Changing the agent is two separate things that only share a name, and they are kept apart here
// because they belong in different places on screen and have different lifetimes.
//
// The list is the long-lived half. The task modal's header caret opens it - that is where the
// design puts every agent list in this modal - so it has to be mounted whenever the header might
// offer that caret: while the thread is folded, while a run is in flight, on a turn that failed.
// Mounting it beside the button meant it came and went with whatever card row happened to hold the
// button, and a caret aimed at a list that is not mounted does nothing at all.
//
// The button is the short-lived half. It belongs in one card's action row, and only on a turn that
// has an answer to run a different agent against.

export interface ChangeAgentPickerProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx). The key the trigger below and the
	// modal header's caret both toggle.
	instanceKey?: string;
	// See NovaAgentPickerProps.anchorInstanceKey: which control the list hangs from, when that is
	// not the trigger. The design opens it under the task modal's own Action/Continue control.
	anchorInstanceKey?: string;
	// How the list lines up with that control — 'right' where it sits at the edge of the modal.
	align?: NovaAgentPickerAlign;
	currentAgentId: string;
	hasReplies: boolean;
	// Whether this person may act on the agent at all. Checked here rather than only on the button:
	// the list opens from the modal header's caret as well, so a guard that lived on the button
	// would be no guard at all for anyone who reached the list the other way.
	canAct?: boolean;
	onAgentChanged: (newAgentId: string) => void;
}

export const ChangeAgentPicker = (props: ChangeAgentPickerProps): JSX.Element => {
	const handleSelect = (selectedAgentId: string): void => {
		if (props.canAct === false) return;
		if (selectedAgentId === props.currentAgentId) return;
		if (props.hasReplies) {
			showChangeAgentConfirmationModal({onConfirm: () => props.onAgentChanged(selectedAgentId)});
		} else {
			props.onAgentChanged(selectedAgentId);
		}
	};

	return (
		<NovaAgentPicker
			taskId={props.taskId}
			instanceKey={props.instanceKey}
			anchorInstanceKey={props.anchorInstanceKey}
			align={props.align}
			onSelectAgent={handleSelect}
		/>
	);
};

export interface ChangeAgentTriggerProps {
	taskId: string;
	// The key this opens. Must match the picker's own, which is what listens for the toggle.
	instanceKey?: string;
	// Shown, but refused, with the reason on it — changing the agent starts a fresh run, so it
	// needs the same permission acting on the task needs. The picker refuses on the same question;
	// this only keeps a control that cannot act from looking like one that can.
	disabled?: boolean;
	disabledReason?: string;
}

// Two arrows pointing opposite ways, the shape the design draws for swapping one agent for another.
// The design system has no swap or exchange glyph of its own; its sort icon is that same pair of
// arrows standing on end, so a quarter turn lays them side by side rather than hand-drawing a second
// copy of an asset the system already owns.
//
// currentColor rather than one of the icon's own colours: the button it sits in is drawn in three
// tones and greys out when refused, and the arrows have no reason to state a colour of their own in
// any of those.
//
// A wrapper rather than styled(Icon) because the icon's own prop types are not assignable to what
// styled() accepts, and the turn belongs to this one use of the asset anyway.
const SwapIcon = styled.span`
	display: inline-flex;
	flex: none;
	transform: rotate(90deg);
`;

export const ChangeAgentTrigger = (props: ChangeAgentTriggerProps): JSX.Element => {
	const {formatMessage} = useIntl();

	return (
		// The same compact outlined control the answer card's other actions use, in its neutral
		// tone: changing the agent is not the card's primary act, and a text link among three
		// bordered buttons reads as a different kind of thing than it is.
		<NovaCompactButton
			tone="neutral"
			onPress={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
			disabled={props.disabled}
			title={props.disabled ? props.disabledReason : undefined}
			data-cy="nova-change-agent"
			id={anchorElementId(props.taskId, props.instanceKey)}
		>
			{/* Hidden from a screen reader: the label beside it already says what the control does. */}
			<SwapIcon aria-hidden="true">
				<Icon icon="sort" size="s" customColor="currentColor" />
			</SwapIcon>
			{formatMessage({id: 'nova_invocation.change_agent.button'})}
		</NovaCompactButton>
	);
};
